import {FieldValues} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import {uploadFilesAndGetTicket, postHelpForm} from '@/api/help/form';
import {FormType} from '@/components/help/common/FormContainer/types';
import FormSubmitSuccessDialog from '@/components/help/common/FormSuccessDialog/FormSubmitSuccessDialog';
import {SubmitFormPayload} from '@/api/help/form/type';
import {
  FORM_TYPE_INQUIRY,
  FORM_TYPE_ILLEGAL,
  FORM_TYPE_OBJECTION,
} from '@/constants/help/form';
import {useHelpContext} from '@/app/(main)/routes/help/context/HelpContext';
import {useMobileInfo} from './useMobileInfo';
import {useAutoCloseDialog} from './useAutoCloseDialog';

/**
 * 파일 업로드를 포함한 폼 제출 과정을 처리하는 커스텀 Hook
 */
export const useSubmitForm = ({type}: {type: FormType}) => {
  const {openDialog} = useAutoCloseDialog();
  const {userId, isMobile} = useHelpContext();
  const mobileInfo = useMobileInfo();

  const showSuccessDialog = () => {
    openDialog({
      ignoreOutsideClick: true,
      displayType: 'center',
      scrollLock: true,
      component: () => <FormSubmitSuccessDialog type={type} userId={userId} />,
    });
  };

  const createPayload = (
    formData: FieldValues,
    ticketId: string | undefined,
  ): SubmitFormPayload => {
    const nodeId =
      type === FORM_TYPE_INQUIRY || type === FORM_TYPE_ILLEGAL
        ? formData.subCategory
        : formData.mainCategory;

    return {
      formType: type,
      isMobile,
      nodeId,
      customerEmail: formData.customerEmail,
      title: formData.title,
      content: formData.content,
      isGuest: !userId, // userId가 없으면 비회원
      ticketId,
    };
  };

  return useMutation({
    mutationFn: async (formData: FieldValues) => {
      // 모바일 정보 추가
      // 이의신청은 모바일정보 필요 없음
      if (mobileInfo && type !== FORM_TYPE_OBJECTION) {
        formData.content += mobileInfo;
      }

      // 1) 파일 업로드
      const ticketId = await uploadFilesAndGetTicket(formData.attachedFiles);

      // 2) 페이로드 생성
      const payload = createPayload(formData, ticketId);

      return postHelpForm(payload);
    },
    onSuccess: (response) => {
      if (response?.errorCode === 0) {
        showSuccessDialog();
      } else {
        console.error(`오류가 발생했습니다. (코드: ${response?.errorCode})`);
      }
    },
    onError: (error: Error) => {
      console.error('문의 제출 과정 실패:', error);
    },
  });
};
